<template>
  <div class="transaction-history">
    <div class="d-flex justify-space-between align-center mb-6">
      <h2 class="text-h5">Histórico de Transações</h2>
      <v-btn
        color="primary"
        variant="tonal"
        prepend-icon="mdi-download"
        @click="exportTransactions"
      >
        Exportar
      </v-btn>
    </div>

    <!-- Filtros e busca -->
    <div class="filters mb-6">
      <v-row>
        <v-col cols="12" sm="4">
          <v-text-field
            v-model="searchQuery"
            label="Buscar transação"
            density="compact"
            variant="outlined"
            prepend-inner-icon="mdi-magnify"
            hide-details
            clearable
            @update:model-value="applyFilters"
          ></v-text-field>
        </v-col>

        <v-col cols="12" sm="3">
          <v-select
            v-model="filterType"
            :items="transactionTypes"
            item-title="label"
            item-value="value"
            :label="t('gov.type')"
            density="compact"
            variant="outlined"
            hide-details
            @update:model-value="applyFilters"
          >
            <template v-slot:selection="{ item }">
              <div class="d-flex align-center">
                <v-icon :color="getTypeColor(item.raw.value)" class="mr-2" size="small">
                  {{ getTypeIcon(item.raw.value) }}
                </v-icon>
                {{ item.raw.label }}
              </div>
            </template>
          </v-select>
        </v-col>

        <v-col cols="12" sm="3">
          <v-select
            v-model="filterStatus"
            :items="statusOptions"
            item-title="label"
            item-value="value"
            :label="t('tx.status')"
            density="compact"
            variant="outlined"
            hide-details
            @update:model-value="applyFilters"
          >
            <template v-slot:selection="{ item }">
              <div class="d-flex align-center">
                <v-icon :color="getStatusColor(item.raw.value)" class="mr-2" size="small">
                  {{ getStatusIcon(item.raw.value) }}
                </v-icon>
                {{ item.raw.label }}
              </div>
            </template>
          </v-select>
        </v-col>

        <v-col cols="12" sm="2">
          <v-menu
            v-model="dateMenu"
            :close-on-content-click="false"
          >
            <template v-slot:activator="{ props }">
              <v-btn
                variant="outlined"
                color="primary"
                class="w-100"
                v-bind="props"
                prepend-icon="mdi-calendar"
                density="compact"
              >
                {{ dateRangeText }}
              </v-btn>
            </template>
            <v-card min-width="300">
              <v-card-title>Período</v-card-title>
              <v-card-text>
                <v-date-picker
                  v-model="dateRange"
                  range
                  color="primary"
                  @update:model-value="applyFilters"
                ></v-date-picker>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="primary" @click="clearDateFilter">Limpar</v-btn>
                <v-btn color="primary" @click="dateMenu = false">Fechar</v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>
        </v-col>
      </v-row>
    </div>

    <!-- Estatísticas -->
    <v-row class="transaction-stats mb-4">
      <v-col cols="12" md="3">
        <v-card class="pa-3 text-center h-100" elevation="1">
          <div class="text-overline mb-1">Total de Transações</div>
          <div class="text-h5 font-weight-bold">{{ filteredTransactions.length }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card class="pa-3 text-center h-100" elevation="1">
          <div class="text-overline mb-1">Volume 7 dias</div>
          <div class="text-h5 font-weight-bold">{{ formatAmount(volumeLastWeek) }} {{ coin }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card class="pa-3 text-center h-100" elevation="1">
          <div class="text-overline mb-1">Valor Médio</div>
          <div class="text-h5 font-weight-bold">{{ formatAmount(averageAmount) }} {{ coin }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card class="pa-3 text-center h-100" elevation="1">
          <div class="text-overline mb-1">Taxas Pagas</div>
          <div class="text-h5 font-weight-bold">{{ formatAmount(totalFees) }} {{ coin }}</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Tabela de transações -->
    <v-card>
      <v-data-table
        :headers="headers"
        :items="filteredTransactions"
        :loading="isLoading"
        :items-per-page="10"
        :items-per-page-options="[10, 20, 50, 100]"
        class="transaction-table"
      >
        <!-- Coluna Data/Hora -->
        <template v-slot:item.timestamp="{ item }">
          <div>
            <div>{{ formatDate(item.raw.timestamp) }}</div>
            <div class="text-caption">{{ formatTime(item.raw.timestamp) }}</div>
          </div>
        </template>

        <!-- Coluna Tipo -->
        <template v-slot:item.type="{ item }">
          <div class="d-flex align-center">
            <v-icon :color="getTypeColor(item.raw.type)" class="mr-2">
              {{ getTypeIcon(item.raw.type) }}
            </v-icon>
            <span>{{ getTypeLabel(item.raw.type) }}</span>
          </div>
        </template>

        <!-- Coluna De/Para -->
        <template v-slot:item.addresses="{ item }">
          <div>
            <div class="d-flex align-center mb-1">
              <span class="text-caption mr-1">De:</span>
              <span class="address-text">{{ shortenAddress(item.raw.from) }}</span>
              <v-icon
                size="x-small"
                color="primary"
                class="ml-1 copy-icon"
                @click="copyToClipboard(item.raw.from)"
              >
                mdi-content-copy
              </v-icon>
            </div>
            <div class="d-flex align-center">
              <span class="text-caption mr-1">Para:</span>
              <span class="address-text">{{ shortenAddress(item.raw.to) }}</span>
              <v-icon
                size="x-small"
                color="primary"
                class="ml-1 copy-icon"
                @click="copyToClipboard(item.raw.to)"
              >
                mdi-content-copy
              </v-icon>
            </div>
          </div>
        </template>

        <!-- Coluna Valor -->
        <template v-slot:item.amount="{ item }">
          <div :class="item.raw.type === 'receive' ? 'text-success' : ''">
            {{ item.raw.type === 'receive' ? '+' : '' }}{{ formatAmount(item.raw.amount) }} {{ coin }}
          </div>
        </template>

        <!-- Coluna Taxas -->
        <template v-slot:item.fee="{ item }">
          {{ formatAmount(item.raw.fee) }} {{ coin }}
        </template>

        <!-- Coluna Status -->
        <template v-slot:item.status="{ item }">
          <v-chip
            :color="getStatusColor(item.raw.status)"
            size="small"
            class="text-uppercase"
          >
            <v-icon start size="x-small">{{ getStatusIcon(item.raw.status) }}</v-icon>
            {{ getStatusLabel(item.raw.status) }}
          </v-chip>
        </template>

        <!-- Sem dados -->
        <template v-slot:no-data>
          <div class="pa-6 text-center">
            <v-icon size="64" color="grey" class="mb-4">mdi-database-off-outline</v-icon>
            <h3 class="text-h5 mb-2">Nenhuma transação encontrada</h3>
            <p class="text-body-1">Tente remover os filtros ou alterar o período de busca.</p>
          </div>
        </template>

        <!-- Coluna Ações -->
        <template v-slot:item.actions="{ item }">
          <div class="d-flex">
            <v-tooltip text="Ver detalhes">
              <template v-slot:activator="{ props }">
                <v-btn
                  variant="text"
                  color="primary"
                  size="small"
                  icon="mdi-information-outline"
                  v-bind="props"
                  @click="showTransactionDetails(item.raw)"
                  class="mr-1"
                ></v-btn>
              </template>
            </v-tooltip>
            <v-tooltip text="Ver no explorador">
              <template v-slot:activator="{ props }">
                <v-btn
                  variant="text"
                  color="primary"
                  size="small"
                  icon="mdi-open-in-new"
                  v-bind="props"
                  @click="openInExplorer(item.raw.txHash)"
                  class="mr-1"
                ></v-btn>
              </template>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Diálogo de detalhes da transação -->
    <v-dialog
      v-model="detailsDialog.show"
      max-width="700"
    >
      <v-card v-if="detailsDialog.transaction">
        <v-card-title class="d-flex align-center">
          <v-icon :color="getTypeColor(detailsDialog.transaction.type)" class="mr-2">
            {{ getTypeIcon(detailsDialog.transaction.type) }}
          </v-icon>
          {{ getTypeLabel(detailsDialog.transaction.type) }}
          <v-spacer></v-spacer>
          <v-btn icon="mdi-close" variant="text" @click="detailsDialog.show = false"></v-btn>
        </v-card-title>
        
        <v-card-text>
          <!-- Implementação dos detalhes da transação será adicionada posteriormente -->
          <p>Detalhes da transação serão implementados na próxima etapa.</p>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { useAppStore } from '@/stores/app'
import { useDexStore } from '@/stores/dex'

export default {
  name: 'TransactionHistory',
  
  data() {
    return {
      isLoading: false,
      searchQuery: '',
      filterType: null,
      filterStatus: null,
      dateRange: [],
      dateMenu: false,
      
      headers: [
        { title: 'Data/Hora', key: 'timestamp', align: 'start', sortable: true },
        { title: 'Tipo', key: 'type', align: 'start', sortable: true },
        { title: 'De/Para', key: 'addresses', align: 'start', sortable: false },
        { title: 'Valor', key: 'amount', align: 'end', sortable: true },
        { title: 'Taxa', key: 'fee', align: 'end', sortable: true },
        { title: 'Status', key: 'status', align: 'center', sortable: true },
        { title: 'Ações', key: 'actions', align: 'center', sortable: false }
      ],
      
      transactionTypes: [
        { label: 'Todos', value: null },
        { label: 'Envio', value: 'send' },
        { label: 'Recebimento', value: 'receive' },
        { label: 'Swap', value: 'swap' },
        { label: 'Adicionar Liquidez', value: 'add_liquidity' },
        { label: 'Remover Liquidez', value: 'remove_liquidity' },
        { label: 'Stake', value: 'stake' },
        { label: 'Unstake', value: 'unstake' },
        { label: 'Reclamar Recompensas', value: 'claim_rewards' }
      ],
      
      statusOptions: [
        { label: 'Todos', value: null },
        { label: 'Concluído', value: 'completed' },
        { label: 'Pendente', value: 'pending' },
        { label: 'Falha', value: 'failed' }
      ],
      
      detailsDialog: {
        show: false,
        transaction: null
      },
      
      // Transações carregadas da API real do kernel
      transactions: []
    }
  },
  
  computed: {
    appStore() {
      return useAppStore()
    },
    
    dexStore() {
      return useDexStore()
    },
    
    filteredTransactions() {
      let result = [...this.transactions]
      
      // Filtro por tipo de transação
      if (this.filterType) {
        result = result.filter(tx => tx.type === this.filterType)
      }
      
      // Filtro por status
      if (this.filterStatus) {
        result = result.filter(tx => tx.status === this.filterStatus)
      }
      
      // Filtro por data
      if (this.dateRange && this.dateRange.length === 2) {
        const startDate = new Date(this.dateRange[0])
        startDate.setHours(0, 0, 0, 0)
        
        const endDate = new Date(this.dateRange[1])
        endDate.setHours(23, 59, 59, 999)
        
        result = result.filter(tx => {
          const txDate = new Date(tx.timestamp)
          return txDate >= startDate && txDate <= endDate
        })
      }
      
      // Filtro por termo de busca
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        result = result.filter(tx => 
          tx.from.toLowerCase().includes(query) || 
          tx.to.toLowerCase().includes(query) ||
          tx.txHash.toLowerCase().includes(query)
        )
      }
      
      return result.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
    },
    
    dateRangeText() {
      if (!this.dateRange || this.dateRange.length !== 2) {
        return 'Período'
      }
      
      return `${this.formatShortDate(this.dateRange[0])} - ${this.formatShortDate(this.dateRange[1])}`
    },
    
    volumeLastWeek() {
      const oneWeekAgo = new Date()
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7)
      
      return this.transactions
        .filter(tx => new Date(tx.timestamp) >= oneWeekAgo && tx.status === 'completed')
        .reduce((sum, tx) => sum + tx.amount, 0)
    },
    
    averageAmount() {
      if (this.filteredTransactions.length === 0) return 0
      
      const totalAmount = this.filteredTransactions.reduce((sum, tx) => sum + tx.amount, 0)
      return totalAmount / this.filteredTransactions.length
    },
    
    totalFees() {
      return this.filteredTransactions.reduce((sum, tx) => sum + tx.fee, 0)
    }
  },
  
  methods: {
    applyFilters() {
      // Os filtros são aplicados automaticamente através do computed filteredTransactions
    },
    
    clearDateFilter() {
      this.dateRange = []
      this.dateMenu = false
      this.applyFilters()
    },
    
    formatDate(timestamp) {
      return new Date(timestamp).toLocaleDateString('pt-BR')
    },
    
    formatTime(timestamp) {
      return new Date(timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    },
    
    formatShortDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
    },
    
    formatAmount(value) {
      return parseFloat(value).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    
    shortenAddress(address) {
      if (!address || address.startsWith('POOL:') || address.startsWith('STAKE:')) {
        return address
      }
      return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`
    },
    
    getTypeIcon(type) {
      const icons = {
        send: 'mdi-arrow-top-right',
        receive: 'mdi-arrow-bottom-left',
        swap: 'mdi-swap-horizontal',
        add_liquidity: 'mdi-water-plus',
        remove_liquidity: 'mdi-water-minus',
        stake: 'mdi-safe-square',
        unstake: 'mdi-safe-square-outline',
        claim_rewards: 'mdi-gift-outline'
      }
      return icons[type] || 'mdi-help-circle-outline'
    },
    
    getTypeColor(type) {
      const colors = {
        send: 'primary',
        receive: 'success',
        swap: 'purple',
        add_liquidity: 'indigo',
        remove_liquidity: 'blue',
        stake: 'amber',
        unstake: 'orange',
        claim_rewards: 'green'
      }
      return colors[type] || 'grey'
    },
    
    getTypeLabel(type) {
      const typeObj = this.transactionTypes.find(t => t.value === type)
      return typeObj ? typeObj.label : 'Desconhecido'
    },
    
    getStatusIcon(status) {
      const icons = {
        completed: 'mdi-check-circle',
        pending: 'mdi-clock-outline',
        failed: 'mdi-alert-circle'
      }
      return icons[status] || 'mdi-help-circle-outline'
    },
    
    getStatusColor(status) {
      const colors = {
        completed: 'success',
        pending: 'warning',
        failed: 'error'
      }
      return colors[status] || 'grey'
    },
    
    getStatusLabel(status) {
      const statusObj = this.statusOptions.find(s => s.value === status)
      return statusObj ? statusObj.label : 'Desconhecido'
    },
    
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.appStore.showSnackbar({
          text: 'Endereço copiado para a área de transferência',
          color: 'success'
        })
      })
    },
    
    showTransactionDetails(transaction) {
      this.detailsDialog.transaction = transaction
      this.detailsDialog.show = true
    },
    
    openInExplorer(txHash) {
      // Em produção, direcionar para o explorer real
      const baseUrl = 'https://explorer.quarnx.com/tx/'
      window.open(baseUrl + txHash, '_blank')
    },
    
    exportTransactions() {
      // Implementação básica para exportar para CSV
      let csvContent = 'data:text/csv;charset=utf-8,'
      
      // Cabeçalho
      csvContent += 'Data,Hora,Tipo,De,Para,Valor,Taxa,Status,Hash\n'
      
      // Linhas de dados
      this.filteredTransactions.forEach(tx => {
        const date = this.formatDate(tx.timestamp)
        const time = this.formatTime(tx.timestamp)
        const typeLabel = this.getTypeLabel(tx.type)
        const statusLabel = this.getStatusLabel(tx.status)
        
        csvContent += `${date},${time},${typeLabel},${tx.from},${tx.to},${tx.amount},${tx.fee},${statusLabel},${tx.txHash}\n`
      })
      
      // Criar o link para download
      const encodedUri = encodeURI(csvContent)
      const link = document.createElement('a')
      link.setAttribute('href', encodedUri)
      link.setAttribute('download', `quarnx-transactions-${new Date().toISOString().split('T')[0]}.csv`)
      document.body.appendChild(link)
      
      // Simular clique e remover o link
      link.click()
      document.body.removeChild(link)
      
      this.appStore.showSnackbar({
        text: 'Transações exportadas com sucesso',
        color: 'success'
      })
    }
  },
  
  async mounted() {
    this.isLoading = true
    try {
      const { ExplorerAPI } = await import('@/services/api')
      const txs = await ExplorerAPI.transacoes.obterUltimas(10)
      this.transactions = txs.map((tx, i) => ({
        id: i + 1,
        type: tx.type || 'send',
        from: tx.from || tx.de || '',
        to: tx.to || tx.para || '',
        amount: parseFloat(tx.value || tx.valor || 0),
        fee: parseFloat(tx.fee || tx.taxaGas || 0),
        timestamp: tx.timestamp ? new Date(tx.timestamp) : new Date(),
        status: tx.status || 'completed',
        txHash: tx.hash || ''
      }))
    } catch (e) {
      console.error('Erro ao carregar transações:', e)
      this.transactions = []
    } finally {
      this.isLoading = false
    }
  }
}
</script>

<style scoped>
.transaction-history {
  width: 100%;
}

.address-text {
  font-family: 'Roboto Mono', monospace;
  font-size: 0.85rem;
}

.copy-icon {
  opacity: 0.5;
  cursor: pointer;
  transition: opacity 0.2s;
}

.copy-icon:hover {
  opacity: 1;
}

.max-width-300 {
  max-width: 300px;
}

.max-width-200 {
  max-width: 200px;
}
</style>
